import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import { getUserType } from '../../../store/actions/ChatAction';
import { fetchAllEmployees } from '../../../store/actions/organization/employeeActions';
import Header from '../../shared/TailwindComponents/Header';
import Table from '../../shared/TailwindComponents/Table';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import PeopleForm from './PeopleForm';
import { PiArrowSquareInLight, PiArrowSquareOutLight, PiPlusLight, PiUsersLight } from 'react-icons/pi';
import { hasAccessToFeature } from '../../../utils/permissionUtils';
import ProfileImage from '../../Header/ProfileImage';
import Badge from '../../shared/TailwindComponents/Badge';
import { Tooltip } from '@mui/material';
import ExternalPeopleTab from './External/ExternalPeopleList';
import ExternalOnboarding from './External/ExternalOnboarding';
import Button from '../../shared/TailwindComponents/Button';
import debounce from 'lodash.debounce';
import { fetchAllDepartments } from '../../../store/actions/organization/DepartmentActions';
import { formatDate, formatRole } from '../../../utils/functions';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, peopleListTourSteps } from '../../shared/FeatureTour';
import { normalise } from '../../shared/access/accessModel';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, Well } from '../../shared/brand/Field';
import { LayoutContext } from '../../../contexts/LayoutContext';

export interface EmployeeRow {
  id: string;
  Name: React.ReactNode;
  Role: React.ReactNode;
  Departments: React.ReactNode;
  Teams: React.ReactNode;
  'Join Date': string;
}

interface EmployeeListProps {
  isAddingEmployee?: boolean;
  setIsAddingEmployee?: (value: boolean) => void;
  subscriptionActive?: boolean;
}

type TabType = 'internal' | 'external';

/**
 * The group badges for one person - the departments (or teams) they are on,
 * two of them, then a `+N` carrying the rest on hover.
 *
 * This was written TWICE, once in the table mapping and again inline in the
 * grid branch, and the two copies had already drifted: the table drew
 * `<Badge>`s and the grid drew hand-rolled `px-2 py-1 bg-gray-100` spans. One
 * builder, used by both, so a change to how a membership reads happens once.
 */
const GroupBadges: React.FC<{ groups: any[] }> = ({ groups }) => {
  const list = Array.isArray(groups) ? groups : [];
  if (list.length === 0) return <span className="text-fg-muted text-xs">—</span>;
  return (
    <>
      {list.slice(0, 2).map((g: any) => (
        <Badge key={g.id || g.name} color="gray">{g.name}</Badge>
      ))}
      {list.length > 2 && (
        <Tooltip title={list.slice(2).map((g: any) => g.name).join(', ')} placement="top" arrow>
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-fg-muted bg-surface-sunken rounded cursor-default">
            +{list.length - 2}
          </span>
        </Tooltip>
      )}
    </>
  );
};

/** The groups of a person, as the row shape both views render. */
const groupsOf = (employee: any, key: 'departments' | 'teams'): any[] =>
  Array.isArray(employee?.[key]) ? employee[key] : [];

interface PersonCardData {
  id: string;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar: string | null;
  role?: string;
  pending: boolean;
  departments: any[];
  teams: any[];
  joined: string;
}

/**
 * A person as a card.
 *
 * It was a `p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md` box stacking
 * "Role: / Departments: / Teams: / Joined:" as literal label lines - the
 * exact card the glass standard replaces. Now a `GlassCard`: the avatar and
 * name are the headline, the role is a badge, memberships are the SAME badges
 * the table renders, and the join date is one muted footer line.
 */
const EmployeeGridItem: React.FC<{ person: PersonCardData; onClick: (id: string) => void }> = ({
  person,
  onClick,
}) => {
  const { t } = useTranslation();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(person.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(person.id); } }}
      className="h-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne rounded-lg"
    >
      <GlassCard padding="md" className="h-full transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <ProfileImage
            profilePicture={person.avatar}
            email={person.email}
            initials={person.name.slice(0, 1).toUpperCase() || '?'}
            firstName={person.firstName}
            lastName={person.lastName}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg-strong truncate">{person.name}</p>
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {person.pending
                ? <Badge color="yellow">{t('Invitation sent')}</Badge>
                : person.role
                  ? <Badge color="blue">{formatRole(person.role)}</Badge>
                  : <span className="text-fg-muted text-xs">—</span>}
            </div>
          </div>
        </div>

        <Well className="space-y-2">
          <Field label={t('Departments')}>
            <div className="flex flex-wrap gap-1"><GroupBadges groups={person.departments} /></div>
          </Field>
          <Field label={t('Teams')}>
            <div className="flex flex-wrap gap-1"><GroupBadges groups={person.teams} /></div>
          </Field>
        </Well>

        <p className="text-[11px] text-fg-muted">
          {t('Joined')}: {person.joined ? formatDate(person.joined) : '—'}
        </p>
      </div>
      </GlassCard>
    </div>
  );
};

const EmployeeList: React.FC<EmployeeListProps> = ({ subscriptionActive, isAddingEmployee: isAddingEmployeeProp, setIsAddingEmployee: setIsAddingEmployeeProp }) => {
  const dispatch = useAppDispatch();
  const { loading, total } = useSelector((state: RootState) => state.employees);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [allDepartments, setAllDepartments] = useState<string[]>([]);
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const userType = getUserType();
  const history = useHistory();
  const { t } = useTranslation();
  const employeesPerPage = 50;
  const [isAddingEmployee, setIsAddingEmployee] = useState(isAddingEmployeeProp || false);
  const [isAddingExternal, setIsAddingExternal] = useState(false);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const location = useLocation();
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false); 
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('tab') as TabType) || 'internal';
  });
  const totalActiveFilters = Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);
  const { setIsContentScrollable } = useContext(LayoutContext);

  /* The list scrolls INSIDE itself (see the render), so the page scroller is
     off while it is mounted - the same contract every full-height list on the
     platform uses. */
  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'people-list',
    steps: peopleListTourSteps,
    readyToShow: !loading && !isAddingEmployee && !isAddingExternal,
    autoStartDelay: 600,
  });

  const tabs = [
    { label: t('Internal'), value: 'internal', icon: <PiArrowSquareInLight className="h-4 w-4" /> },
    { label: t('External'), value: 'external', icon: <PiArrowSquareOutLight className="h-4 w-4" /> },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as TabType;
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search, activeTab]);

  // useEffect(() => {
  //   const query = new URLSearchParams(location.search);
  //   if (query.get('add') === 'true') {
  //     setIsAddingEmployee(true);
  //     if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(true);
  //   } else {
  //     setIsAddingEmployee(false);
  //     if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(false);
  //   }
  // }, [location, setIsAddingEmployeeProp]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const deptResp = await dispatch(fetchAllDepartments({ limit: 100 }));
      if (deptResp && Array.isArray(deptResp)) {
        const deptNames = deptResp.map((d: any) => d.Name || d.name).filter((d: any) => d);
        setAllDepartments(deptNames);
      } else if (deptResp && deptResp.departments && Array.isArray(deptResp.departments)) {
        const deptNames = deptResp.departments.map((d: any) => d.Name || d.name).filter((d: any) => d);
        setAllDepartments(deptNames);
      }
      const empResp = await dispatch(fetchAllEmployees({ limit: 1000, filters: { skip_filters: true } }));
      if (empResp && empResp.employees && Array.isArray(empResp.employees)) {
        const uniqueRoles = Array.from(new Set(empResp.employees.map(emp => emp.role || emp.role).filter(role => role)));
        setAllRoles(uniqueRoles);
      }
    };
    fetchInitialData();
  }, [dispatch]);

  const filters = useMemo(() => [
    {
      id: 'Department',
      name: t('Department'),
      options: allDepartments.map(dept => ({ value: dept, label: dept })),
    },
    {
      id: 'Role',
      name: t('Roles'),
      options: allRoles.map(role => ({ value: role, label: role })),
    },
  ], [allDepartments, allRoles, t]);

  const getQueryParams = useCallback(() => {
    const filtersObj: { [key: string]: string[] } = {};
    if (selectedFilters.Department && selectedFilters.Department.length > 0) {
      filtersObj.Department = selectedFilters.Department;
    }
    if (selectedFilters.Role && selectedFilters.Role.length > 0) {
      filtersObj.Roles = selectedFilters.Role;
    }
    return { searchTerm, filters: filtersObj, sortBy, sortOrder, limit: employeesPerPage };
  }, [searchTerm, sortBy, sortOrder, employeesPerPage, selectedFilters]);

  const refetchEmployees = useCallback(async (fetchPage: number = 1) => {
    setIsFetching(true);
    const params = getQueryParams();
    const fullResp = await dispatch(fetchAllEmployees({ ...params, page: fetchPage }));
    if (fullResp && fullResp.employees && Array.isArray(fullResp.employees)) {
      setAllEmployees(fullResp.employees);
      setHasMore(fullResp.employees.length === employeesPerPage && fullResp.currentPage < fullResp.totalPages);
    } else {
      setAllEmployees([]);
      setHasMore(false);
    }
    setIsFetching(false);
  }, [dispatch, getQueryParams, employeesPerPage]);

  const loadMoreEmployees = useCallback(
    debounce(async () => {
      if (!hasMore || loading || isFetching) return;
      setIsFetching(true);
      const nextPage = page + 1;
      const params = getQueryParams();
      const fullResp = await dispatch(fetchAllEmployees({ ...params, page: nextPage }));
      if (fullResp && fullResp.employees && Array.isArray(fullResp.employees)) {
        setAllEmployees(prev => [...prev, ...fullResp.employees]);
        setPage(nextPage);
        setHasMore(fullResp.employees.length === employeesPerPage && fullResp.currentPage < fullResp.totalPages);
      }
      setIsFetching(false);
    }, 300),
    [dispatch, getQueryParams, hasMore, loading, page, employeesPerPage]
  );

  useEffect(() => {
    if (isAddingEmployee || isAddingExternal || isFetching) return;
    setPage(1);
    refetchEmployees(1);
  }, [searchTerm, selectedFilters, sortBy, sortOrder, isAddingEmployee, isAddingExternal, refetchEmployees]);

  const handleAddEmployee = () => {
    setIsAddingEmployee(true);
    if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(true);
    history.push('/people?add=true');
  };

  const handleCloseOnboarding = () => {
    setIsAddingEmployee(false);
    setIsAddingExternal(false);
    if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(false);
    if (activeTab === 'internal') {
      history.push('/people');
    } else {
      history.push('/people?tab=external');
    }
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
    setPage(1);
  };

  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchTerm(query);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (query: string) => {
    debouncedSearch(query);
  };

  const columns = ['Name', 'Role', 'Departments', 'Teams', 'Join Date'];

  const data = useMemo<EmployeeRow[]>(() => {
    if (!Array.isArray(allEmployees)) {
      return [];
    }
    const mappedData = allEmployees.map((employee): EmployeeRow | null => {
      if (!employee) {
        console.warn('Employee is undefined:', employee);
        return null;
      }
      const isActive = employee.is_active !== false;
      const invitationStatus = employee.invitation_status || (isActive ? 'active' : 'pending');
      const firstName = employee.first_name || employee.FirstName || '';
      const lastName = employee.last_name || employee.LastName || '';
      const email = employee.email || employee.Email || '';

      const displayName = firstName && lastName ? `${firstName} ${lastName}` : email;
      const initials = firstName && lastName
        ? `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim()
        : (email?.split('@')[0]?.[0] || '?').toUpperCase();

      const joinDateStr = employee.created_at ? formatDate(employee.created_at) : 'N/A';

      const statusBadge = invitationStatus === 'pending' ? (
        <Badge color="yellow" className="text-xs">{t('Invitation sent')}</Badge>
      ) : null;

      return {
        id: employee.id || employee.UserID,
        Name: (
          <div className="flex items-center space-x-2">
            <ProfileImage
              profilePicture={normalise(employee).avatar}
              email={email}
              initials={initials}
              firstName={firstName}
              lastName={lastName}
            />
            <div>
              <span className={invitationStatus === 'pending' ? 'text-gray-500 dark:text-gray-400' : ''}>{displayName}</span>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {statusBadge}
              </div>
            </div>
          </div>
        ),
        Role: invitationStatus === 'active' && employee.role ? (
          <Badge color="blue">{formatRole(employee.role)}</Badge>
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>
        ),
        Departments: (
          <div className="flex flex-wrap gap-1">
            <GroupBadges groups={groupsOf(employee, 'departments')} />
          </div>
        ),
        Teams: (
          <div className="flex flex-wrap gap-1">
            <GroupBadges groups={groupsOf(employee, 'teams')} />
          </div>
        ),
        'Join Date': joinDateStr,
      };
    }).filter((item): item is EmployeeRow => item !== null);
    return mappedData;
  }, [allEmployees, t]);

  const handleTabClick = (value: string) => {
    setActiveTab(value as TabType);
    const params = new URLSearchParams(location.search);
    params.set('tab', value);
    history.push({ search: params.toString() });
  };

/*
  THE PAGE OWNS ITS HEIGHT. `setIsContentScrollable(false)` (above) turns
  `<main>` into a flex column, and this is the column's child: header and
  filter bar take their band, the list takes the rest and scrolls INSIDE
  itself. What it replaces on the grid was `overflow-auto
  max-h-[calc(100vh-120px)]` - a guess at the height of the chrome above it
  (header, filter bar, license banner, breadcrumbs) that was wrong by exactly
  the amount the last row was cut off by.
*/
return (
  <div className="flex flex-col h-full min-h-0">
    {isAddingEmployee || isAddingExternal ? (
      <div className='w-full flex-1 min-h-0 overflow-y-auto custom-scrollbar-main'>
        {activeTab === 'internal' && <PeopleForm onSuccess={handleCloseOnboarding} onCancel={handleCloseOnboarding} />}
        {activeTab === 'external' && <ExternalOnboarding onClose={handleCloseOnboarding} />}
      </div>
    ) : (
      <>
        <div data-tour-id="people-list-header" className="flex-shrink-0">
          <Header
            actions={activeTab === 'external' ? [
              {
                label: t('Add External'),
                onClick: handleAddEmployee,
                icon: <PiPlusLight className='w-4 h-4' />,
                requiresSubscription: true,
              },
            ] : [
              // Internal tab previously had NO action, so an org admin had no way
              // to invite from the People page (the button existed only on Admin
              // Users). handleAddEmployee opens the PeopleForm invite flow.
              {
                label: t('Invite User'),
                onClick: handleAddEmployee,
                icon: <PiPlusLight className='w-4 h-4' />,
              },
            ]}
            subscriptionActive={subscriptionActive}
          />
        </div>
        <div data-tour-id="people-list-filters" className="flex-shrink-0">
        <FilterBarWithTabs
          itemCount={allEmployees.length}
          totalItemCount={total}
          itemCountLabel="people"
          // tabs={tabs}
          // activeTab={activeTab}
          onTabClick={handleTabClick}
          filters={filters}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSearchChange={handleSearchChange}
          onClearAllFilters={() => setSelectedFilters({})}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
        </div>
        <div className="relative flex-1 min-h-0 flex flex-col">
          <div data-tour-id="people-list-content" className="absolute top-0 start-0 end-0 h-[120px] pointer-events-none" />
          {activeTab === 'internal' && (
            <>

              {viewMode === 'grid' ? (
                /* The grid owns its own scrolling and carries its own gutter
                   top and bottom - no viewport arithmetic, so nothing is cut
                   off however tall the chrome above it turns out to be. */
                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-4 py-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {isFetching && allEmployees.length === 0 ? (
                      <Skeleton variant="grid" rows={3} />
                    ) : (
                      <>
                        {allEmployees.map((employee) => {
                          const isActive = employee.is_active !== false;
                          const invitationStatus = employee.invitation_status || (isActive ? 'active' : 'pending');
                          const firstName = employee.first_name || employee.FirstName || '';
                          const lastName = employee.last_name || employee.LastName || '';
                          const employeeEmail = employee.email || employee.Email || '';
                          const employeeId = employee.id || employee.UserID;

                          return (
                            <EmployeeGridItem
                              key={employeeId}
                              person={{
                                id: employeeId,
                                name: firstName && lastName ? `${firstName} ${lastName}` : employeeEmail,
                                email: employeeEmail,
                                firstName,
                                lastName,
                                avatar: normalise(employee).avatar,
                                role: employee.role,
                                pending: invitationStatus === 'pending',
                                departments: groupsOf(employee, 'departments'),
                                teams: groupsOf(employee, 'teams'),
                                joined: employee.created_at || employee.JoinDate || '',
                              }}
                              onClick={(id) => history.push(`/people/${id}`)}
                            />
                          );
                        })}
                        {allEmployees.length === 0 && (
                          <div className="col-span-full">
                            <EmptyState
                              icon={<PiUsersLight />}
                              headline={searchTerm || totalActiveFilters > 0 ? t('No results found.') : t('You have no people added yet.')}
                              subtext={searchTerm || totalActiveFilters > 0
                                ? t('Try adjusting your search or filters to find what you’re looking for.')
                                : t('People are invited through the Admin Users section.')}
                              button={searchTerm || totalActiveFilters > 0 ? {
                                label: t('Clear Search & Filters'),
                                onClick: () => setSelectedFilters({}),
                              } : undefined}
                            />
                          </div>
                        )}
                      </>
                    )}
                    {hasMore && allEmployees.length > 0 && (
                      <div className="col-span-full flex justify-center py-6 border-t border-hairline-soft">
                        <Button
                          variant="outline"
                          size="small"
                          onClick={loadMoreEmployees}
                          disabled={isFetching}
                          isLoading={isFetching}
                        >
                          {isFetching ? t('Loading...') : t('Load More')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-h-0 flex flex-col">
                  {isFetching && allEmployees.length === 0 ? (
                    <Skeleton variant="table" rows={2} />
                  ) : (
                    <Table
                      columns={columns}
                      data={data}
                      onRowClick={(row) => {
                        const employee = allEmployees.find((emp) => (emp.id || emp.UserID) === row.id);
                        if (employee) {
                          const employeeId = employee.id || employee.UserID;
                          history.push(`/people/${employeeId}`);
                        } else {
                          console.warn('[WARNING] Employee not found for row:', row.id);
                        }
                      }}
                      hasMore={hasMore}
                      isLoadingMore={isFetching && page > 1}
                      onLoadMore={loadMoreEmployees}
                      sortableColumns={['Name', 'Join Date']}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSortChange}
                      resizableColumns={true}
                      setIsShowing={true}
                      total_items={total}
                      total_showing_items={allEmployees.length}
                      showingLabel={t('people')}
                      defaultColumnWidths={{
                        'Name': 260,
                        'Role': 140,
                        'Departments': 240,
                        'Teams': 240,
                        'Join Date': 160,
                      }}
                      emptyState={{
                        icon: <PiUsersLight />,
                        headline: searchTerm || totalActiveFilters > 0 ? t('No results found.') : t('You have no people added yet.'),
                        subtext: searchTerm || totalActiveFilters > 0
                          ? t('Try adjusting your search or filters to find what you’re looking for.')
                          : t('People are invited through the Admin Users section.'),
                        button: searchTerm || totalActiveFilters > 0 ? {
                          label: t('Clear Search & Filters'),
                          onClick: () => setSelectedFilters({}),
                        } : undefined,
                      }}
                    />
                  )}
                </div>
              )}
            </>
          )}
          {activeTab === 'external' && (
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
              <ExternalPeopleTab getQueryParams={getQueryParams} />
            </div>
          )}
        </div>
        <FeatureTour
          steps={peopleListTourSteps}
          run={isTourRunning}
          onCallback={handleTourCallback}
          continuous
          showProgress
          showSkipButton
          disableOverlay
        />
      </>
    )}
  </div>
);
};

export default EmployeeList;
